import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Popover } from 'antd'
import styled from 'styled-components'
import { StatusBadge } from '@lmc/ui-kit/src/dataDisplay'
import { TaskItemResponse, Task, TaskStatus } from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { useMutation } from 'react-query'
import { showToast } from 'common/utils'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { Text } from '@lmc/ui-kit/src/general'
import { theme } from '@lmc/ui-kit'
import PopoverOption from 'components/generic/PopoverOption'
import actions from 'actions'

const taskStatusRoutes = {
  [TaskStatus.completed]: 'complete',
  [TaskStatus.skipped]: 'skip',
  [TaskStatus.pending]: 'incomplete',
}

type StatusDropdownProps = {
  status: TaskItemResponse['calculatedStatus']
  taskId: string
  task: TaskItemResponse
  onSuccess?: () => void
}

const StatusDropdown = ({ status, taskId, task, onSuccess }: StatusDropdownProps) => {
  const [openStatusDropdown, setOpenStatusDropdown] = useState(false)
  const { t } = useTranslation(NAMESPACE.TODOS)
  const dispatch = useDispatch()

  const mutationFunction = (body: {
    statusPath: string
  }): Promise<{
    result: Task
  }> =>
    postJSON({
      method: 'POST',
      url: `list/tasks/${taskId}/${body.statusPath}`,
      json: { note: '' },
    })

  const { mutateAsync } = useMutation(mutationFunction, {
    onSuccess: () => {
      showToast({ title: t(toDosConstants.STATUS_CHANGED) })
      setOpenStatusDropdown(false)
      onSuccess?.()
    },
    onError: (error) => {
      console.error('Failed to update task status:', error)
      setOpenStatusDropdown(false)
    },
  })

  const getStatusOptions = (currentStatus: string) => {
    const statusMap: Record<string, TaskStatus[]> = {
      overdue: [TaskStatus.completed, TaskStatus.skipped],
      pending: [TaskStatus.completed, TaskStatus.skipped],
      completed: [TaskStatus.skipped, TaskStatus.pending],
      skipped: [TaskStatus.completed, TaskStatus.pending],
    }
    return statusMap[currentStatus] || []
  }

  const openLogModalForTask = (statusPath: string) => {
    dispatch(
      actions.addForm({
        formId: 'addLog',
        fields: {
          resident: task.residentData?.id,
          category: task.category,
          item: task.item,
          title: task.title,
          // Store task info for later completion
          _taskId: task.id,
          _taskStatusPath: statusPath,
          _taskOnSuccess: onSuccess,
        } as any,
      })
    )

    // Open the log modal with taskCompletion flag
    dispatch(
      actions.openModal({
        modalId: 'addLog',
        data: {
          taskCompletion: true, // Flag to indicate this is from task completion
        },
      })
    )
  }

  const handleStatusChange = async (newStatus: TaskStatus) => {
    const statusPath =
      taskStatusRoutes[newStatus as keyof typeof taskStatusRoutes]
    
    // Check if we need to open log modal for task completion
    if (newStatus === TaskStatus.completed && task.logScreenPrompt) {
      openLogModalForTask(statusPath)
      setOpenStatusDropdown(false)
      return
    }
    
    // Original flow for non-log tasks
    await mutateAsync({ statusPath })
  }

  const options = getStatusOptions(status)

  if (options.length === 0) {
    return <DesktopStatusBadge status={status} leftText />
  }

  const popoverContent = (
    <div>
      {options.map((option) => (
        <PopoverOption key={option} onClick={() => handleStatusChange(option)}>
          <Text> {t(toDosConstants[option])}</Text>
        </PopoverOption>
      ))}
    </div>
  )

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      placement="bottomLeft"
      open={openStatusDropdown}
      onOpenChange={setOpenStatusDropdown}
    >
      <ClickableStatusBadge
        onClick={() => setOpenStatusDropdown(!openStatusDropdown)}
      >
        <StatusBadgeWrapper isOpen={openStatusDropdown}>
          <DesktopStatusBadge status={status} leftText />
        </StatusBadgeWrapper>
      </ClickableStatusBadge>
    </Popover>
  )
}

const DesktopStatusBadge = styled(StatusBadge)`
  cursor: pointer;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const StatusBadgeWrapper = styled.div<{ isOpen: boolean }>`
  color: ${({ isOpen }) => (isOpen ? theme.color.status.primary : 'inherit')};

  * {
    color: ${({ isOpen }) => (isOpen ? theme.color.status.primary : 'inherit')};
  }
`

const ClickableStatusBadge = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`

export default StatusDropdown
